/**
 * 页面初始化时必要数据获取
 */
import React, { useEffect, useState } from 'react';
import { useAsyncEffect } from 'ahooks';
import {
  PageType,
  TaskFieldType,
  PageConfigType,
  Utils,
  EventType,
} from '@/asset/types';
import {
  getTaskPageCache,
  getPageLowCode,
  getTaskPropertySettings,
} from '@/api/scene';
import { getFsToken } from '@/api/tools';
import { PageInitialization } from '@/utils/PageInitialization';
import useMetaStore, { useMeta } from '@/store/useMetaStore';
import useValuesStore, { useValues } from '@/store/useValuesStore';
import { getMaxFloor, Api as FetchAPI, isMobileEnv } from '@/utils';
import { ROUTERSTATUSMAPPING } from '@/asset/AssetTask/consts';
import { RuleTigger } from '@/const';
import { Rule, Module, ModuleField } from '@/store/type';
import { findAssetCascadeData, getTaskRules } from '@/api/cascade';
import { cascadeTransformValues } from '@/asset/AssetTask/service';
import { copyPageValue } from '@/asset/AssetTask/templates/Cascade/effect/useCopyPage';
import { getBizConfig } from '@/common/asset-apis/getAssetModuleByRequest';
import {
  COLUMN_FROM,
  LCP_EFECT_KINDS,
} from '@/components/SEFieldWithModel/effects/consts';
import { getUserInfoCache } from '@/api/system';
import * as notify from '@/utils/notify';
import _ from 'lodash';

interface StoreInit {
  taskId: string;
  pageType: PageType;
  pageConfig?: PageConfigType;
  isMobile?: boolean;
  utils?: Utils;
  recordId?: string;
  events?: any;
  propValues?: Record<string, any>;
  refleshPageFlag?: boolean;
}

export default function useStoreInit({
  taskId,
  pageType,
  pageConfig: propsPageConfig,
  isMobile = isMobileEnv(),
  utils,
  recordId,
  events,
  auth,
  propValues: _propValues,
  formRef,
}: StoreInit) {
  const [isReady, setIsReady] = useState(false);
  const meta = useMetaStore();
  const { setMetaInfo, metaInfoByRule, dispatchEvent } = useMetaStore();
  const { setRules, resetValue, values, pageRules } = useValuesStore();

  // 将utils写入到window上，给到规则那边可以获取到当前url中参数，在qiankun容器内，会通过代理的形式保证不污染顶层window
  useEffect(() => {
    window.pageProps = { utils, pageType, taskId };
  }, [utils, taskId, pageType]);

  const [propValues, setPropValues] = useState<Record<string, any> | undefined>(
    _propValues,
  );
  useEffect(() => {
    if (_.isEqual(_propValues, propValues)) {
      return;
    }

    setPropValues(_propValues);
  }, [_propValues, propValues]);

  useAsyncEffect(async () => {
    // 清除事件中心的全量数据
    dispatchEvent([], true);
    // 清除页面数据
    resetValue({}, []);
    // 获取用户角色
    const role = await getUserInfoCache(FetchAPI);
    const myRoles = role?.myRoles?.map((item: any) => item.roleNumber);

    // 获取模板接口
    const taskInfo = await getTaskPageCache({ taskId });
    const moduleIdList = taskInfo.modules?.map((item) => item.moduleID);
    // 获取任务下的属性
    const resultTaskFields = (await getTaskPropertySettings(
      taskId,
      moduleIdList,
    )) as TaskFieldType[];
    const moduleTaskField: Record<string, TaskFieldType[]> = {};
    taskInfo.modules?.forEach((module) => {
      moduleTaskField[module.handle] =
        resultTaskFields?.filter(
          (field) => field.logic_entity_id === module.moduleID,
        ) || [];
    });

    // 获取页面的配置
    let pageConfig =
      propsPageConfig ||
      (await getPageLowCode({ pageStatus: pageType, pageKey: taskId }));
    pageConfig.freeBtnConfig = pageConfig.freeBtnConfig
      ? pageConfig.freeBtnConfig.filter((x) => x.visible !== false)
      : [];
    // 如果没有配置会默认生成一份初始化的页面配置信息
    if (Object.keys(pageConfig || {}).length === 0) {
      console.info(
        '%c 此页面没有人工配置,机器人已帮你推送默认配置加载。',
        'color:blue;background:yellow;',
      );
      const config = await new PageInitialization({
        taskId,
        pageType,
        taskTemplateInterface: taskInfo,
        taskFieldInterface: resultTaskFields,
      }).initConfig();
      pageConfig = config;
    }
    pageConfig.rulePageValue = (pageConfig?.rulePageValue || []).filter(
      (rs: any) => rs.visible !== false,
    );

    let fsToken = '';
    if (pageType === PageType.Create) {
      try {
        fsToken = await getFsToken();
      } catch (e) {
        console.error(e);
      }
    }

    // setStore
    setMetaInfo({
      isMobile,
      taskInfo,
      moduleTaskField,
      taskId,
      moduleList: taskInfo.modules,
      utils,
      pageType: ROUTERSTATUSMAPPING[pageType],
      recordId,
      pageConfig,
      eventsConfig: events,
      auth,
      fsToken,
      myRoles,
      formRef,
      // exclusiveConfig: createExclusiveConfig(pageConfig, ROUTERSTATUSMAPPING[pageType]) // 专属当前页面的配置 比如 createPageConfig
    });
    // 规则配置数据获取
    const ruleRes = await getTaskRules({ taskId, sort: 'sequence+' });
    const resultList = ruleRes?.data?.ruleScenes || [];
    const schemaList: Rule[] = resultList
      .filter((r: any) => r.visible !== false)
      .map((item: any) => {
        return {
          RULES: item?.rules_expression || '',
          TODOOPTIONS: item?.result || [],
          trigger: item.trigger || RuleTigger.PageValuesChange,
          triggerConfig: item.triggerConfig,
          message: item.message,
          label: item.label,
          name: item.name,
        };
      });
    const pageRuleList =
      pageConfig?.rulePageValue?.map((item: any) => {
        return {
          RULES: item?.rules.rules_expression || '',
          TODOOPTIONS: item.events?.isMakeEntity
            ? {
                pageEvent: item.events,
                ...item.events?.entityConfig,
              }
            : {
                pageEvent: item.events,
              },
          trigger: item.trigger || RuleTigger.PageValuesChange,
          triggerConfig: item.triggerConfig,
          message: item.message,
          label: item.label,
          name: item.name,
        };
      }) || [];
    const rsList = schemaList.concat(pageRuleList);
    setRules(rsList);

    const propValuesData = propValues ?? {};

    // 拉页面的数据
    if (pageType === 'list') {
      resetValue({}, myRoles);
    } else if (ROUTERSTATUSMAPPING[pageType] === 'add') {
      // 获取复制信息
      const pageInitValues = await copyPageValue({
        pageType,
        pageConfig,
        utils,
        taskInfo,
      });
      resetValue(
        _.merge({ ...pageInitValues }, { ...propValuesData }),
        myRoles,
      );
    } else {
      const res = await findAssetCascadeData({
        taskId,
        recordId,
        version: utils?.queryParams?.version,
        pageModuleConfig: pageConfig.pageModuleConfig,
      });
      const values = cascadeTransformValues(res.response, taskInfo.modules);

      resetValue(_.merge({ ...values }, { ...propValuesData }), myRoles);
    }

    setIsReady(true);
  }, [taskId, pageType, propValues]);
  // 自定义按钮处理
  useEffect(() => {
    if (propsPageConfig) {
      propsPageConfig.freeBtnConfig = propsPageConfig.freeBtnConfig
        ? propsPageConfig.freeBtnConfig.filter((x) => x.visible !== false)
        : [];
      setMetaInfo({
        pageConfig: propsPageConfig,
      });
    }
  }, [propsPageConfig, pageType]);

  // 更新执行的事件
  useEffect(() => {
    setMetaInfo({
      eventsConfig: events,
      isMobile,
      pageType: ROUTERSTATUSMAPPING[pageType],
      utils,
      auth,
    });
  }, [events, isMobile, pageType, utils, auth]);

  // 模型及字段处理
  // useEffect(() => {
  //   if (!meta?.taskInfo || !meta?.pageConfig) {
  //     return;
  //   }
  //   let moduleList: Module[] = _.cloneDeep(meta?.taskInfo?.modules || []);
  //   moduleList.forEach((module) => {
  //     // 标记主模型
  //     if (module.moduleID === meta.taskInfo?.mainModuleId) {
  //       module.isMain = true;
  //     }
  //     const configList = meta?.pageConfig?.pageModuleConfig?.[module.handle]?.configList?.filter((f) => true) || [];
  //     // 筛选出需要显示的字段，基于配置字段排序
  //     const fields: ModuleField[] = [];
  //     configList
  //       .forEach((config) => {
  //         const field = module.fields.find(f => f.name === config.name);
  //         if (!field) {
  //           // 配置中字段还存在，但是逻辑实体上面已经没有该字段了
  //           notify.error(notify.Type.FieldNotExistInLogic, { name: config.name, label: config.label });
  //           return null;
  //         }
  //         let isDisabled = field.isDisabled || false;
  //         let isRequired = field.isRequired || false;
  //         let search = false;
  //         let searchDefaultValue = null;
  //         let width = null;
  //         let visible = true;
  //         let colProps = {};
  //         const taskField = meta?.moduleTaskField?.[module.handle].find((tf) => tf.field_code === field.name);
  //         if (taskField?.field_from === 'quote') {
  //           isDisabled = true;
  //         }
  //         if (taskField?.required) {
  //           isRequired = true;
  //         }
  //         const filedConfig = configList.find((fc) => fc.name === field.name);
  //         if (filedConfig?.showSearch) {
  //           search = true;
  //         }
  //         if (filedConfig?.showSource === false || field.visible === false) {
  //           visible = false;
  //         }
  //         if (filedConfig?.searchOptions?.defaultValue) {
  //           searchDefaultValue = filedConfig?.searchOptions?.defaultValue;
  //         }
  //         if (filedConfig?.showOptions?.width) {
  //           width = filedConfig?.showOptions?.width;
  //         }
  //         if (filedConfig?.showOptions?.isSingleLine) {
  //           colProps = { span: 24 };
  //         }
  //         fields.push({
  //           // isEditable: field.isEditable,
  //           isRequired,
  //           isDisabled,
  //           name: field.name,
  //           dataIndex: field.name,
  //           fieldID: field.fieldID,
  //           label: field.label,
  //           componentsCode: field.componentsCode,
  //           options: field.options,
  //           kind: field.kind,
  //           visible,
  //           search,
  //           searchOptions: filedConfig?.searchOptions,
  //           searchDefaultValue,
  //           width,
  //           colProps,
  //         });

  //         // 处理RecordSelect联动显示字段
  //         if (field?.kind !== 'OpenApiSelect') {
  //           const { showFields = [], value: linkByFieldName } = ((field.options?.componentsOptions || {}) as { showFields: []; value: string });
  //           showFields?.forEach((showField: { key: any; realLabel: any; showModule: any; showModuleLabel: any; showModuleValue?: string }) => {
  //             fields.push({
  //               kind: LCP_EFECT_KINDS.LINK_SHOW_RECORD,
  //               name: showField?.key,
  //               linkBy: field.name,
  //               label: showField?.realLabel,
  //               linkByFieldName,
  //               linkByLabel: field.label,
  //               linkByField: field,
  //               showModule: showField?.showModule,
  //               showModuleLabel: showField?.showModuleLabel,
  //               showModuleValue: showField?.showModuleValue,
  //               showNamespace: showField?.showNamespace,
  //               isImg: showField?.isImg,
  //               visible: true,
  //             });
  //           });
  //         }
  //       });
  //     module.fields = fields;
  //   });

  //   // TODO 基于规则生成的meta信息过滤模型
  //   moduleList = moduleList.filter((model) => {
  //     if (_.get(metaInfoByRule, [model.handle, 'visible']) === false) {
  //       return false;
  //     }
  //     return true;
  //   });

  //   // 基于规则结果变更
  //   Object.keys(metaInfoByRule).forEach((handle) => {
  //     const module = moduleList.find((m) => m.handle === handle);
  //     if (!module) {
  //       return;
  //     }
  //     // 规则变更的字段处理
  //     metaInfoByRule[handle].fields.forEach(ruleField => {
  //       const field = module?.fields.find((field) => field.dataIndex === ruleField.dataIndex);
  //       if (field) {
  //         if (ruleField.bizComponent !== undefined) {
  //           field.bizComponent = ruleField.bizComponent;
  //           let bizConfig = getBizConfig(ruleField.bizComponent.componentsCode);
  //           if (!bizConfig) {
  //             // notify.error(notify.Type.BizComponentNotFind, `字段：${field.label}, 业务组件：${ruleField.bizComponent.componentsCode}`);
  //             bizConfig = {};
  //           }
  //           field.kind = bizConfig.kind;
  //           field.componentsCode = ruleField.bizComponent.componentsCode;
  //           field.bizConfig = Object.assign({}, bizConfig.options, ruleField.bizComponent?.options);
  //           field.extension = Object.assign({}, field.extension, {
  //             tooltip: ruleField.bizComponent?.tooltip,
  //             isDisabled: !ruleField.bizComponent?.isEditable,
  //           });
  //         }
  //         if (ruleField.isDisabled !== undefined) {
  //           field.isDisabled = ruleField.isDisabled;
  //         }
  //         if (ruleField.isRequired !== undefined) {
  //           field.isRequired = ruleField.isRequired;
  //         }
  //         if (ruleField.visible === false) {
  //           field.visible = false;
  //         }
  //       }
  //       // 由各个数据使用方自行筛选
  //       // module.fields = module?.fields.filter(field => field.visible !== false);
  //     });
  //   });

  //   setMetaInfo({ moduleList });
  // }, [JSON.stringify(metaInfoByRule), meta.taskInfo, meta.pageConfig]);

  return {
    isReady,
  };
}
